'use client'
import * as React from 'react'
import { createStore, useStore } from 'zustand'
import { Role } from '@basehub/data/replicache/index'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserStore } from '~/app/_store/user-store'
import { useActivePublicTeam } from '~/hooks-data/team/use-active-public-team'
import {
  hasAccessToRepo,
  isGuestInRepo,
} from '@basehub/data/replicache/mutators/team-space/team/auth'

export type UserRoleStore = {
  userRole: Role
  setUserRole: (newRole: Role) => void
}

const createUserRoleStore = (initProps?: Partial<UserRoleStore>) => {
  return createStore<UserRoleStore>()((set) => ({
    userRole: 'member',
    setUserRole: (newRole) => set({ userRole: newRole }),
    ...initProps,
  }))
}

const UserRoleStoreContext = React.createContext<ReturnType<
  typeof createUserRoleStore
> | null>(null)

export function useUserRoleStore<T>(selector: (state: UserRoleStore) => T): T {
  const store = React.useContext(UserRoleStoreContext)
  if (!store) {
    throw new Error('Missing UserRoleStoreContext.Provider in the tree')
  }
  return useStore(store, selector)
}

export const UserRoleStoreProvider = ({
  children,
  initialValues,
}: {
  children: React.ReactNode
  initialValues?: Partial<UserRoleStore>
}) => {
  const store = React.useMemo(
    () => createUserRoleStore(initialValues),
    [initialValues]
  )

  const setUserRole = useStore(store, (state) => state.setUserRole)

  const { data: teamReplicache, isFetching: isTeamFetching } =
    useTeamReplicache()
  const { data: repoReplicache } = useRepoReplicache()
  const userId = useUserStore((state) => state.optimisticUser?.id)
  const { team: publicTeam } = useActivePublicTeam().data ?? {}

  React.useEffect(() => {
    if (teamReplicache && userId) {
      const teamMember = teamReplicache.team.members.find(
        (member) => member.userId === userId
      )

      if (!isTeamFetching && !teamMember) {
        console.warn("User isn't a member of the team.")
        const timeout = setTimeout(() => {
          window.location.href = '/dashboard'
        }, 3000)

        return () => clearTimeout(timeout)
      } else if (!teamMember) return

      const roleByRepoIdOrDefault = hasAccessToRepo(
        teamMember,
        repoReplicache?.repo?.id
      )
      if (!roleByRepoIdOrDefault) {
        console.warn("User doesn't have a role in the repo")
        window.location.href = `/${teamReplicache.team.slug}`
        return
      }

      setUserRole(teamMember.role)
    } else if (publicTeam) {
      if (
        repoReplicache?.repo &&
        userId &&
        isGuestInRepo({
          repo: repoReplicache.repo,
          userId,
          allowVisitorInPublicRepos: true,
        })
      ) {
        setUserRole('viewer')
      }
    }
  }, [
    setUserRole,
    publicTeam,
    teamReplicache,
    repoReplicache,
    userId,
    isTeamFetching,
  ])

  return (
    <UserRoleStoreContext.Provider value={store}>
      {children}
    </UserRoleStoreContext.Provider>
  )
}
